import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { keys, useMessages, useSessions } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { formatTime, initials } from "../../lib/format";
import type { ChatMessage, Conversation } from "../../lib/types";
import { Avatar, Empty } from "../ui/Badges";
import { ExtendDialog } from "../cashier/SessionDialogs";

const PRESETS = [
  { code: "TPL_EXPIRY_WARNING_V2", key: "whatsapp.preset.warning", tone: "bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100" },
  { code: "TPL_OVERDUE_V1", key: "whatsapp.preset.timeUp", tone: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100" },
  { code: "TPL_EXTENDED_V1", key: "whatsapp.preset.extended", tone: "bg-purple-50 text-cosmic-purple border-purple-200 hover:bg-purple-100" },
] as const;

function Ticks({ status }: { status: ChatMessage["status"] }) {
  if (status === "queued") return <span aria-label="queued">⏳</span>;
  if (status === "failed") return <span aria-label="failed" className="text-red-500">⚠️</span>;
  return <span className={status === "read" ? "text-sky-500" : "text-gray-400"}>{status === "sent" ? "✓" : "✓✓"}</span>;
}

export function ChatThread({ conversation }: { conversation: Conversation }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isManager } = useAuth();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const g = conversation.guardian;
  const { data: messages } = useMessages(g.id);
  const sessions = useSessions();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [extend, setExtend] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages?.length, g.id]);

  const session = sessions.data?.sessions.find((s) => s.id === conversation.session?.id);

  async function send(body: Record<string, unknown>) {
    setBusy(true);
    try {
      await api.post("/whatsapp/messages", { guardianId: g.id, ...body });
      await qc.invalidateQueries({ queryKey: keys.messages(g.id) });
      void qc.invalidateQueries({ queryKey: keys.conversations });
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    await send({ text: text.trim(), sessionId: conversation.session?.id });
    setText("");
  }

  async function retry(id: string) {
    try {
      await api.post(`/whatsapp/messages/${id}/retry`);
      toast(t("whatsapp.retried"), "info");
      void qc.invalidateQueries({ queryKey: keys.messages(g.id) });
    } catch (e) {
      toastError(e);
    }
  }

  return (
    <div className="flex h-[400px] flex-col justify-between p-3.5">
      <div className="mb-2 flex items-center justify-between border-b border-gray-100 pb-2">
        <div className="flex items-center gap-2">
          <Avatar text={initials(g.fullName)} />
          <div>
            <div className="text-xs font-black text-cosmic-navy">{g.fullName} <span className="rounded bg-gray-100 px-1 text-[9px] font-bold text-gray-500">{g.locale.toUpperCase()}</span></div>
            <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="num">{g.phone}</span>
              {conversation.session && <span> • {t("table.tag")} <span className="num">{conversation.session.wristbandTag}</span></span>}
            </div>
          </div>
        </div>
        {session && <button type="button" className="rounded-lg bg-purple-100 px-2.5 py-1 text-[11px] font-black text-cosmic-purple hover:bg-purple-200" onClick={() => setExtend(true)}>{t("whatsapp.extendBtn")}</button>}
      </div>

      <div className="flex-1 space-y-2.5 overflow-y-auto pe-1" aria-live="polite">
        {!messages?.length && <Empty>{t("whatsapp.noMessages")}</Empty>}
        {messages?.map((m) => {
          const mine = m.direction === "out";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[85%] space-y-1 rounded-2xl border p-2.5 text-xs shadow-sm ${mine ? "rounded-ss-none border-purple-200 bg-purple-50" : "rounded-se-none border-transparent bg-cosmic-purple text-white"}`}>
                {mine && <div className="text-[10px] font-bold text-cosmic-purple">🚀 {m.templateCode ? t("whatsapp.automated") : t("whatsapp.station")}</div>}
                <p className={`whitespace-pre-wrap ${mine ? "text-gray-800" : ""}`} dir="auto">{m.body}</p>
                <div className={`flex items-center justify-end gap-1.5 text-[9px] ${mine ? "text-gray-400" : "text-purple-200"}`}>
                  <span className="num">{formatTime(m.createdAt, locale)}</span>
                  {mine && <Ticks status={m.status} />}
                  {mine && m.status === "failed" && isManager && <button type="button" className="font-black text-red-600 underline" onClick={() => retry(m.id)}>{t("whatsapp.retry")}</button>}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="border-t border-gray-100 pt-2">
        {conversation.session && (
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 text-[10px] font-bold">
            {PRESETS.map((p) => (
              <button key={p.code} type="button" disabled={busy} onClick={() => send({ templateCode: p.code, sessionId: conversation.session!.id })} className={`whitespace-nowrap rounded-full border px-2.5 py-1 ${p.tone}`}>
                {t(p.key)}
              </button>
            ))}
          </div>
        )}
        <form onSubmit={submit} className="flex gap-2">
          <input className="field flex-1 !py-1.5" dir="auto" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("whatsapp.typeMessage")} aria-label={t("whatsapp.typeMessage")} disabled={!g.whatsappOptIn} />
          <button type="submit" className="btn-primary" disabled={busy || !text.trim() || !g.whatsappOptIn}>{t("whatsapp.send")}</button>
        </form>
        {!g.whatsappOptIn && <p className="mt-1 text-[10px] font-bold text-red-600">{t("whatsapp.optedOut")}</p>}
      </div>
      {extend && session && sessions.data && <ExtendDialog session={session} rules={sessions.data.rules} onClose={() => setExtend(false)} />}
    </div>
  );
}
